import { createContext, use, useEffect, useState, useCallback } from "react";
import Cookies from "js-cookie";
import { useLocation, useNavigate } from "react-router";
import { APIGetMyTests, APIGetUserProfile, APILogin, APILogout, APIVerifyToken, APICreateGrandSlamInvoice, APIGetTrialStatus } from "./api";
import { CssBaseline, CssVarsProvider, Typography } from "@mui/joy";
import LandingPage from "./pages/LandingPage";
import { theme } from "./App";

// Публичные страницы, доступные без авторизации
const PUBLIC_ROUTES = ['/fanki', '/fanki/', '/pro', '/pro/', '/grand-slam', '/grand-slam/', '/system', '/system/'];



export const LayoutContext = createContext();


export default function LayoutProvider({ children }) {
    const navigate = useNavigate();
    const location = useLocation();

    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [isVerified, setIsVerified] = useState(false);
    const [isVerifying, setIsVerifying] = useState(true);
    const [loading, setLoading] = useState(false);

    // Состояние модального окна профиля
    const [modalAccountOpen, setModalAccountOpen] = useState(false);

    // Тесты пользователя
    const [ tests, setTests ] = useState([]);
    const [ loadingTests, setLoadingTests ] = useState(true);

    const [generateTestModalOpen, setGenerateTestModalOpen] = useState(false);

    const [successPaymentModalOpen, setSuccessPaymentModalOpen] = useState(false);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);

    // Sidebar visibility (full-width mode)
    const [sidebarHidden, setSidebarHidden] = useState(false);

    // Trial state — 5-дневный триал (state, days_left, ends_at, russian_content_access)
    // state: 'paid' | 'active_trial' | 'expired' | 'not_started'
    const [trialInfo, setTrialInfo] = useState(null);

    // Upgrade modal (открывается при 403 trial_locked или кликах на заблоченные разделы)
    const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
    const [upgradeModalReason, setUpgradeModalReason] = useState('');


    // Проверка токена при монтировании
    useEffect(() => {
        const verifyToken = async () => {
        try {
            const cookieToken = Cookies.get('authToken');
            
            if (!cookieToken) {
            throw new Error('Токен не найден');
            }
            
            const response = await APIVerifyToken(cookieToken);
            
            setToken(cookieToken);
            setUser(response.user);
            setIsVerified(true);
            
        } catch (error) {
            console.error('Ошибка верификации:', error);
            setIsVerified(false);
            // Очищаем невалидный токен
            Cookies.remove('authToken');
            setToken(null);
            setUser(null);
        } finally {
            setIsVerifying(false);
        }
        };
        
        verifyToken();
    }, [location]);

    // Загрузка данных профиля при изменении пользователя
    useEffect(() => {
        if (user && token) {
            loadUserProfile();
            loadTrialStatus();
        }
    }, [user?.id, token]);

    const loadUserProfile = async () => {
        try {
            const response = await APIGetUserProfile(token);
            if (response.success) {
                // Обновляем пользователя с полными данными профиля
                setUser(prev => ({ ...prev, ...response.user }));
            }
        } catch (error) {
            console.error('Error loading user profile:', error);
        }
    };

    // Загрузить актуальный trial state с бэка. Вызывается после логина,
    // после save-survey (старт триала), и после успешной оплаты.
    const loadTrialStatus = useCallback(async () => {
        if (!token) return null;
        try {
            const data = await APIGetTrialStatus(token);
            setTrialInfo(data);
            return data;
        } catch (error) {
            console.error('Error loading trial status:', error);
            return null;
        }
    }, [token]);

    // Открыть UpgradeModal. `reason` — короткое сообщение для контекста
    // ("Этот модуль недоступен в триале" и т.п.).
    // Для tg_promo юзеров (Telegram-подписчики закрытого класса) модалка
    // открывается с TG_PROMO_PLANS (1590/3990/6990) вместо Grand Slam — см.
    // UpgradeModal.jsx, где выбор плана зависит от trialInfo.is_tg_promo.
    const openUpgradeModal = useCallback((reason = '') => {
        setUpgradeModalReason(reason);
        setUpgradeModalOpen(true);
    }, []);

    const closeUpgradeModal = useCallback(() => {
        setUpgradeModalOpen(false);
        setUpgradeModalReason('');
    }, []);
    
    // Функция авторизации
    const login = async (email, password) => {
        setLoading(true);
        try {
            const response = await APILogin(email, password);

            setToken(response.token);
            setUser(response.user);
            setIsVerified(true);

            // Сохраняем токен в куки
            Cookies.set('authToken', response.token, { expires: 1 });

            // Если пользователь пришёл с Grand Slam лендинга и нажал "Оплатить"
            // до логина — подхватываем pending_plan и сразу редиректим на YooKassa.
            const pendingPlan = localStorage.getItem('pending_plan');
            if (pendingPlan) {
                localStorage.removeItem('pending_plan');
                try {
                    const period = parseInt(pendingPlan, 10);
                    if ([1, 3, 6, 12].includes(period)) {
                        const data = await APICreateGrandSlamInvoice(response.token, period);
                        if (data?.payment_url) {
                            window.location.href = data.payment_url;
                            return { success: true, data: response, redirecting: true };
                        }
                    }
                } catch (e) {
                    console.error('Не удалось создать платёж Grand Slam после логина:', e);
                    // Fall through — обычная навигация, юзер сможет повторить оплату
                }
            }

            return { success: true, data: response };
        } catch (error) {
            console.error('Ошибка авторизации:', error);
            const serverError = error.response?.data?.error;
            const errorMap = {
                'Invalid credentials': 'Неверный email или пароль',
                'User not found': 'Пользователь не найден',
                'Account is not active': 'Аккаунт не активирован',
            };
            const message = errorMap[serverError] || serverError || 'Ошибка авторизации';
            return {
                success: false,
                error: message
            };
        } finally {
            setLoading(false);
        }
    };

    // Функция выхода
    const logout = () => {
        APILogout();
        setToken(null);
        setUser(null);
        setIsVerified(false);
        // Очищаем токен
        Cookies.remove('authToken');
        navigate('/login');
    };

    // Функции для управления модальным окном профиля
    const openModalAccount = () => {
        setModalAccountOpen(true);
    };

    const closeModalAccount = () => {
        setModalAccountOpen(false);
    };

    const loadUserTests = async () => {
        setLoadingTests(true);
        try {
            const data = await APIGetMyTests(token);
            if (data.success) {
                setTests(data.tests);
            }
        } catch (error) {
            console.error('Error fetching tests:', error);
        } finally {
            setLoadingTests(false);
        }
    };

    // Проверка ролей
    const hasRole = (role) => {
        return user?.role === role;
    };

    const isAdmin = () => {
        return user?.role === 'admin';
    };

    // Обновление данных пользователя
    const updateUser = (userData) => {
        setUser(prev => ({ ...prev, ...userData }));
    };


    const values = {
        // Auth state
        user,
        token,
        isVerified,
        isVerifying,
        loading,
        
        // Auth methods
        login,
        logout,
        hasRole,
        isAdmin,

        // Модальное окно профиля
        modalAccountOpen,
        setModalAccountOpen: openModalAccount, closeModalAccount,
        
        tests, setTests, loadUserTests,
        loadingTests, setLoadingTests,

        // Обновление пользователя
        updateUser,

        generateTestModalOpen,
        setGenerateTestModalOpen,
        successPaymentModalOpen, setSuccessPaymentModalOpen,
        paymentModalOpen, setPaymentModalOpen,

        sidebarHidden, setSidebarHidden,

        // Trial state + upgrade modal
        trialInfo,
        loadTrialStatus,
        upgradeModalOpen,
        upgradeModalReason,
        openUpgradeModal,
        closeUpgradeModal,
    };
    
    return (
        <CssVarsProvider
            theme={theme}
        >
            <CssBaseline />
            <LayoutContext.Provider value={values}>
                {PUBLIC_ROUTES.includes(location.pathname) ? children :
                isVerifying ? <VerificationScreen /> :
                isVerified ? children : <LandingPage />}

                {/* {children} */}
            </LayoutContext.Provider>
        </CssVarsProvider>
    );
}


// Экран верификации
function VerificationScreen() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: 'var(--joy-palette-background-body, #FAF9F6)'
    }}>
      <Typography level="h4" sx={{ color: 'text.secondary' }}>Загрузка...</Typography>
    </div>
  );
}